import Button from '../../../../../components/button/Button';
import CommonTable from '../../../../../components/common-table/CommonTable';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import moment from '../../../../../common/constants/moment';
import { commonDateMonthFormat } from '../../../../../common/constants/dateConstants';
import {
    addPensionItem,
    removePensionItem,
    updatePensionItem,
} from '../../../../../redux/reducers/onboarding/sliceReducer';
import PensionInput from './PensionInput';
import CommonDatePicker from '../../../../../components/date-picker/CommonDatePicker';
import { Select } from 'antd';
import AngleIcon from '../../../../../assets/icons/AngleIcon';
import { useState } from 'react';
import DeletePensionModal from './DeletePensionModal';
import { openSuccessNotification } from '../../../../../components/notification/Notification';
import { LPPLevel } from '../../../../../common/api/services/OnboardingApi/enums/OnboardingLPPLevel';
import TrashIcon from '../../../../../assets/icons/TrashIcon';
import { Roles } from '../../../../../common/enums/Roles';
import CrossIcon from '../../../../../assets/icons/CrossIcon';

interface IProps {
    getError: (fieldName: string) => string;
}

const PensionTable = (props: IProps) => {
    const { getError } = props;
    const dispatch = useAppDispatch();
    const { plans } = useAppSelector((state) => state.onboarding.ssnInformation);
    const [selectedDelete, setSelectedDelete] = useState<number | null>(null);
    const { role } = useAppSelector((state) => state.auth);
    const isAdmin = role === Roles.ADMIN || role === Roles.MANAGER;

    const { personalInfo } = useAppSelector((state) => state.onboarding);
    const { workForm } = personalInfo;
    const isCorporation = workForm === 'corporation';

    const initialItem = {
        lppLevel: undefined,
        startDate: null,
        endDate: null,
        annualSalaryAmount: '',
        occupationDegree: '',
        employerContribution: null,
        employeeContribution: null,
    };

    const onClose = () => {
        setSelectedDelete(null);
    };

    const onDelete = () => {
        if (selectedDelete !== null) {
            dispatch(removePensionItem(selectedDelete));
            openSuccessNotification('The plan was deleted successfully.');
            onClose();
        }
    };

    const data: any = plans?.map((item, index) => {
        return {
            startDate: item?.startDate ? moment(item.startDate).format(commonDateMonthFormat) : '',
            endDate: item?.endDate ? moment(item.endDate).format(commonDateMonthFormat) : '',
            lppLevel: item?.lppLevel || '',
            annualSalaryAmount: item?.annualSalaryAmount || '',
            occupationDegree: item?.occupationDegree || '',
            employerContribution: item?.employerContribution,
            employeeContribution: item?.employeeContribution,
            key: index,
        };
    });

    const columns: any = [
        {
            title: 'Plan',
            dataIndex: 'lppLevel',
            width: 125,
            render: (text: string, data: any) => (
                <Select
                    options={Object.values(LPPLevel).map((item) => {
                        return {
                            value: item,
                            label: item,
                        };
                    })}
                    onChange={(value: any) =>
                        dispatch(
                            updatePensionItem({
                                index: data.key,
                                key: 'lppLevel',
                                value,
                            })
                        )
                    }
                    className="tax-dropdown"
                    style={{
                        width: '100%',
                        border: getError(`data.plans[${data.key}].lppLevel`) ? '1px solid #e02b1d' : '',
                        borderRadius: getError(`data.plans[${data.key}].lppLevel`) ? '5px' : '',
                    }}
                    suffixIcon={<AngleIcon />}
                    value={text}
                    disabled={isCorporation}
                />
            ),
        },
        {
            title: 'Annual salary',
            dataIndex: 'annualSalaryAmount',
            width: 90,
            render: (text: string, data: any) => (
                <PensionInput
                    index={data.key}
                    keyValue="annualSalaryAmount"
                    value={text}
                    getError={getError}
                    disabled={isCorporation}
                />
            ),
        },
        {
            title: 'Start date',
            dataIndex: 'startDate',
            width: 125,
            render: (text: any, data: any) => {
                return (
                    <CommonDatePicker
                        label=""
                        changeDate={(date) =>
                            dispatch(
                                updatePensionItem({
                                    index: data.key,
                                    key: 'startDate',
                                    value: date ? moment(date).format(commonDateMonthFormat) : '',
                                })
                            )
                        }
                        defaultValue={data.startDate}
                        bodyContainer
                        style={{
                            border: getError(`data.plans[${data.key}].startDate`) ? '1px solid #e02b1d' : '',
                            borderRadius: getError(`data.plans[${data.key}].startDate`) ? '5px' : '',
                        }}
                        disabled={isCorporation}
                    />
                );
            },
        },
        {
            title: 'End date',
            dataIndex: 'endDate',
            width: 125,
            render: (text: any, data: any) => (
                <CommonDatePicker
                    label=""
                    changeDate={(date) =>
                        dispatch(
                            updatePensionItem({
                                index: data.key,
                                key: 'endDate',
                                value: date ? moment(date).format(commonDateMonthFormat) : '',
                            })
                        )
                    }
                    defaultValue={data.endDate}
                    bodyContainer
                    style={{
                        border: getError(`data.plans[${data.key}].endDate`) ? '1px solid #e02b1d' : '',
                        borderRadius: getError(`data.plans[${data.key}].endDate`) ? '5px' : '',
                    }}
                    disabled={isCorporation}
                />
            ),
        },

        {
            title: 'Occupation degree (%)',
            dataIndex: 'occupationDegree',
            width: 100,
            render: (text: string, data: any) => (
                <PensionInput
                    index={data.key}
                    keyValue="occupationDegree"
                    value={text}
                    getError={getError}
                    disabled={isCorporation}
                />
            ),
        },
        {
            title: 'Employer cotisation',
            dataIndex: 'employerContribution',
            width: 110,
            render: (text: string, data: any) => (
                <PensionInput
                    index={data.key}
                    keyValue="employerContribution"
                    value={text}
                    getError={getError}
                    disabled={isCorporation}
                />
            ),
        },
        {
            title: 'Employee cotisation',
            dataIndex: 'employeeContribution',
            width: 135,
            render: (text: string, data: any) => (
                <PensionInput
                    index={data.key}
                    keyValue="employeeContribution"
                    value={text}
                    getError={getError}
                    disabled={isCorporation}
                />
            ),
        },
        {
            title: 'Action',
            dataIndex: 'action',
            width: 60,
            render: (text: string, data: any) => {
                return (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <TrashIcon
                            onClick={() => {
                                if (!isCorporation) {
                                    setSelectedDelete(data.key);
                                }
                            }}
                        />
                    </div>
                );
            },
        },
    ];

    return (
        <div className="tax-table-wrapper">
            {getError('data.plans') && (
                <span className="error-message error-text">
                    <div className="icon-wrapper">
                        <CrossIcon />
                    </div>
                    {getError('data.plans')}
                </span>
            )}
            <CommonTable
                columns={
                    isAdmin
                        ? columns
                        : columns.filter((item: any) =>
                              ['lppLevel', 'action', 'startDate', 'endDate'].includes(item.dataIndex)
                          )
                }
                data={data}
                tableHeight={300}
                height={300}
                hidePagination
                hasRowDatePicker
                rowKey="key"
                key="key"
                isBorderedTable
            />
            <Button
                buttonSize="medium"
                buttonStyle="primary"
                disable={isCorporation}
                buttonWidth={'100%'}
                className={`add-tax-item-btn-`}
                onClick={() => {
                    if (!isCorporation) dispatch(addPensionItem(initialItem));
                }}
            >
                + Add line
            </Button>
            {selectedDelete !== null && (
                <DeletePensionModal isOpen={selectedDelete !== null} onClose={onClose} onSubmit={onDelete} />
            )}
        </div>
    );
};

export default PensionTable;
